import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';

const CompanionUpgrade: React.FC = () => {
  const { companions, upgradeCompanions, upgradeToMysticDivine, formatNumber } = useGameStore();
  const [selectedType, setSelectedType] = useState<Companion['type'] | null>(null);
  const [selectedCompanions, setSelectedCompanions] = useState<string[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [showMysticDivine, setShowMysticDivine] = useState(false);

  const getCompanionsByType = (type: Companion['type']) => {
    // Filtere göttliche Begleiter aus für Upgrade-Zwecke
    return companions.discovered.filter(companion => 
      companion.type === type && !(companion.name.startsWith('★') && companion.name.endsWith('★'))
    );
  };

  const getDivineLegendaryCompanions = () => {
    // Zähle göttlich legendäre Begleiter (mit ★ Sternen), aber NICHT mystisch göttliche (mit ★★★ Sternen)
    return companions.discovered.filter(companion => 
      companion.type === 'legendary' && 
      companion.name.startsWith('★') && companion.name.endsWith('★') &&
      !(companion.name.startsWith('★★★') && companion.name.endsWith('★★★'))
    );
  };

  const getRarityColor = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'from-yellow-400 to-orange-500';
      case 'epic': return 'from-purple-400 to-pink-500';
      case 'rare': return 'from-blue-400 to-cyan-500';
      case 'common': return 'from-gray-400 to-gray-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getRarityName = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'Legendär';
      case 'epic': return 'Episch';
      case 'rare': return 'Selten';
      case 'common': return 'Gewöhnlich';
      default: return 'Unbekannt';
    }
  };

  const getAbilityIcon = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return '👆';
      case 'coinPerSecond': return '💰';
      case 'gemChance': return '💎';
      case 'xpBonus': return '⭐';
      case 'gardenBonus': return '🌱';
      case 'autoClickPower': return '🤖';
      case 'upgradeDiscount': return '🏷️';
      case 'prestigeBonus': return '🔄';
      case 'criticalHit': return '⚔️';
      case 'healing': return '❤️';
      case 'shield': return '🛡️';
      case 'counterAttack': return '↩️';
      case 'lifeSteal': return '🩸';
      case 'armorPenetration': return '🗡️';
      default: return '✨';
    }
  };

  const getAbilityName = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return 'Klick-Bonus';
      case 'coinPerSecond': return 'Münzen/s';
      case 'gemChance': return 'Edelstein-Chance';
      case 'xpBonus': return 'Erfahrung';
      case 'gardenBonus': return 'Garten-Bonus';
      case 'autoClickPower': return 'Auto-Klick';
      case 'upgradeDiscount': return 'Upgrade-Rabatt';
      case 'prestigeBonus': return 'Prestige-Bonus';
      case 'criticalHit': return 'Kritischer Treffer';
      case 'healing': return 'Heilung';
      case 'shield': return 'Schild';
      case 'counterAttack': return 'Gegenangriff';
      case 'lifeSteal': return 'Lebensraub';
      case 'armorPenetration': return 'Rüstungsdurchschlag';
      default: return 'Unbekannt';
    }
  };

  const getAbilitySuffix = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return 'x';
      case 'coinPerSecond': return '/s';
      case 'gemChance': return '%';
      case 'xpBonus': return '%';
      case 'gardenBonus': return '%';
      case 'autoClickPower': return '';
      case 'upgradeDiscount': return '%';
      case 'prestigeBonus': return '%';
      case 'criticalHit': return '%';
      case 'healing': return '';
      case 'shield': return '';
      case 'counterAttack': return '%';
      case 'lifeSteal': return '%';
      case 'armorPenetration': return '%';
      default: return '';
    }
  };

  const getUpgradeTarget = (type: Companion['type']) => {
    switch (type) {
      case 'common': return { type: 'rare' as const, name: 'Selten' };
      case 'rare': return { type: 'epic' as const, name: 'Episch' };
      case 'epic': return { type: 'legendary' as const, name: 'Legendär' };
      case 'legendary': return { type: 'legendary' as const, name: 'Göttlich' };
      default: return null;
    }
  };

  const handleTypeSelect = (type: Companion['type']) => {
    setSelectedType(type);
    setSelectedCompanions([]);
  };

  const handleCompanionToggle = (companionId: string) => {
    setSelectedCompanions(prev => {
      if (prev.includes(companionId)) {
        return prev.filter(id => id !== companionId);
      } else if (prev.length < 5) {
        return [...prev, companionId];
      }
      return prev;
    });
  };

  const handleUpgrade = () => {
    if (!selectedType || selectedCompanions.length !== 5) return;
    
    // Verwende die erweiterte upgradeCompanions Funktion mit spezifischen IDs
    const success = upgradeCompanions(selectedType, selectedCompanions);
    if (success) {
      // Prüfe, ob noch weitere Begleiter zum Upgraden verfügbar sind
      // Verwende den aktuellen Store-Zustand nach dem Upgrade
      const currentCompanions = companions.discovered.filter(companion => 
        companion.type === selectedType && !(companion.name.startsWith('★') && companion.name.endsWith('★'))
      );
      
      if (currentCompanions.length >= 5) {
        // Es gibt noch genug Begleiter für ein weiteres Upgrade - bleibe im Fenster
        setSelectedCompanions([]);
        console.log(`Erfolgreich ${selectedType} Begleiter upgegraded! Weitere verfügbar.`);
      } else {
        // Keine weiteren Begleiter verfügbar - schließe das Fenster
        setSelectedType(null);
        setSelectedCompanions([]);
        console.log(`Erfolgreich ${selectedType} Begleiter upgegraded! Keine weiteren verfügbar.`);
      }
    }
  };

  const handleBackToOverview = () => {
    setSelectedType(null);
    setSelectedCompanions([]);
  };

  const handleRandomSelect = () => {
    if (!selectedType) return;
    
    const availableCompanions = getCompanionsByType(selectedType);
    if (availableCompanions.length < 5) return;
    
    // Mische die verfügbaren Begleiter und wähle die ersten 5
    const shuffled = [...availableCompanions].sort(() => Math.random() - 0.5);
    const randomSelection = shuffled.slice(0, 5).map(c => c.id);
    
    setSelectedCompanions(randomSelection);
  };

  const handleClearSelection = () => {
    setSelectedCompanions([]);
  };

  const upgradeTypes: Companion['type'][] = ['common', 'rare', 'epic', 'legendary'];

  // Wenn mystisch göttlich Fusion ausgewählt ist
  if (showMysticDivine) {
    const divineCompanions = getDivineLegendaryCompanions();
    const selectedDivineCompanions = selectedCompanions.length;
    const canUpgrade = selectedDivineCompanions >= 50;
    
    return (
      <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-xl p-3 sm:p-6 border-2 border-purple-400 shadow-lg mb-4 sm:mb-6">
        {/* Header mit Zurück-Button */}
        <div className="flex items-center justify-between mb-4">
          <motion.button
            onClick={() => {
              setShowMysticDivine(false);
              setSelectedCompanions([]);
            }}
            className="bg-purple-700/50 rounded-lg p-2 border border-purple-600 text-white text-sm flex items-center gap-2"
            whileTap={{ scale: 0.98 }}
          >
            ← Zurück
          </motion.button>
          <h3 className="text-lg sm:text-xl font-bold text-white text-center flex-1">
            🔮 Mystisch Göttlich Fusion
          </h3>
          <div className="w-16"></div>
        </div>

        {/* Fortschrittsanzeige */}
        <div className="bg-black/20 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-sm">Ausgewählt:</span>
            <span className="text-white font-bold">{selectedDivineCompanions}/50</span>
          </div>
          <div className="w-full bg-black/30 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(selectedDivineCompanions / 50) * 100}%` }}
            />
          </div>
        </div>

        {/* Zufällig auswählen Button */}
        {divineCompanions.length >= 50 && (
          <div className="mb-4">
            <motion.button
              onClick={() => {
                const shuffled = [...divineCompanions].sort(() => Math.random() - 0.5);
                const randomSelection = shuffled.slice(0, 50).map(c => c.id);
                setSelectedCompanions(randomSelection);
              }}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 px-4 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="text-lg"
                >
                  🎲
                </motion.div>
                <span className="text-sm sm:text-base">50 zufällig auswählen</span>
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="text-lg"
                >
                  🎲
                </motion.div>
              </div>
            </motion.button>
          </div>
        )}

        {/* Begleiter-Auswahl Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 p-2">
          {divineCompanions.map((companion) => {
            const isSelected = selectedCompanions.includes(companion.id);
            const canSelect = !isSelected && selectedDivineCompanions < 50;
            
            return (
              <motion.div
                key={companion.id}
                onClick={() => {
                  if (canSelect) {
                    setSelectedCompanions(prev => [...prev, companion.id]);
                  } else if (isSelected) {
                    setSelectedCompanions(prev => prev.filter(id => id !== companion.id));
                  }
                }}
                className="relative group cursor-pointer"
                whileHover={canSelect || isSelected ? { scale: 1.05, y: -2 } : {}}
                whileTap={canSelect || isSelected ? { scale: 0.95 } : {}}
                layout
              >
                <div className={`
                  relative bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50 hover:shadow-xl transition-all duration-300 overflow-hidden
                  ${isSelected 
                    ? 'ring-2 ring-purple-400 ring-opacity-80 bg-purple-500/10' 
                    : canSelect 
                      ? 'hover:bg-white/90' 
                      : 'opacity-50 cursor-not-allowed bg-gray-500/50'
                  }
                `}>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 via-transparent to-pink-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-2 -right-2 bg-purple-500 rounded-full w-6 h-6 flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-lg z-10"
                    >
                      ✓
                    </motion.div>
                  )}
                  
                  <div className="relative text-center mb-4">
                    <div className="text-5xl mb-3 drop-shadow-lg">
                      {companion.icon.startsWith('data:') ? (
                        <img 
                          src={companion.icon} 
                          alt={companion.name}
                          className="w-16 h-16 object-contain"
                          style={{ display: 'inline-block' }}
                        />
                      ) : (
                        companion.icon
                      )}
                    </div>
                    <h4 className="text-lg font-bold text-gray-800 leading-tight mb-2 break-words">{companion.name}</h4>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="inline-flex items-center gap-1 bg-yellow-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                        <span>👑</span>
                        <span>Göttlich</span>
                      </div>
                    </div>
                  </div>

                  <div className={`py-2 px-3 rounded-xl font-bold text-sm transition-colors text-center ${
                    isSelected 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' 
                      : canSelect 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg' 
                        : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  }`}>
                    {isSelected ? '✓ Ausgewählt' : canSelect ? 'Auswählen' : 'Max erreicht'}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Hinweis */}
        <div className="mt-3 bg-purple-600/20 border border-purple-400 rounded-lg p-2">
          <div className="text-purple-300 text-xs">
            💡 Wähle genau 50 göttlich legendäre Begleiter für die mystisch göttlich Fusion aus!
            <br />
            ⚠️ Mystisch göttliche Begleiter (★★★) können nicht erneut fusioniert werden.
          </div>
        </div>

        {/* Fusion Button - Desktop */}
        <div className="hidden sm:block">
          <motion.button
            onClick={() => {
              if (canUpgrade) {
                const success = upgradeToMysticDivine(selectedCompanions);
                if (success) {
                  // Nur das Menü verlassen wenn keine weiteren Fusionen möglich sind
                  const remainingDivineCompanions = getDivineLegendaryCompanions().length;
                  if (remainingDivineCompanions < 50) {
                    setShowMysticDivine(false);
                  }
                  setSelectedCompanions([]);
                }
              }
            }}
            disabled={!canUpgrade}
            className={`
              w-full py-4 px-6 rounded-xl font-bold text-lg transition-all
              ${canUpgrade
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
              }
            `}
            whileHover={canUpgrade ? { scale: 1.02 } : {}}
            whileTap={canUpgrade ? { scale: 0.98 } : {}}
          >
            {canUpgrade 
              ? '🔮 Mystisch Göttlich Fusion!' 
              : `Wähle ${50 - selectedDivineCompanions} weitere Begleiter`
            }
          </motion.button>
        </div>

        {/* Floating Fusion Button - Mobile */}
        <AnimatePresence key="mystic-divine-fusion-button">
          {canUpgrade && (
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.8 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 25,
                duration: 0.3 
              }}
              className="fixed bottom-4 left-4 right-4 z-50 sm:hidden"
            >
              <motion.button
                onClick={() => {
                  const success = upgradeToMysticDivine(selectedCompanions);
                  if (success) {
                    // Nur das Menü verlassen wenn keine weiteren Fusionen möglich sind
                    const remainingDivineCompanions = getDivineLegendaryCompanions().length;
                    if (remainingDivineCompanions < 50) {
                      setShowMysticDivine(false);
                    }
                    setSelectedCompanions([]);
                  }
                }}
                className="w-full py-4 px-6 rounded-xl font-bold text-lg shadow-2xl border-2 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-700 text-white border-white/20 backdrop-blur-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(147, 51, 234, 0.3)"
                }}
              >
                <div className="flex items-center justify-center gap-3">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="text-xl"
                  >
                    🔮
                  </motion.div>
                  <span>Mystisch Göttlich Fusion!</span>
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="text-xl"
                  >
                    🔮
                  </motion.div>
                </div>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Wenn ein Typ ausgewählt ist, zeige die Begleiter-Auswahl
  if (selectedType) {
    const availableCompanions = getCompanionsByType(selectedType);
    const target = getUpgradeTarget(selectedType);
    
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 sm:p-6 border border-gray-600 mb-4 sm:mb-6">
        {/* Header mit Zurück-Button */}
        <div className="flex items-center justify-between mb-4">
          <motion.button
            onClick={handleBackToOverview}
            className="bg-gray-700/50 rounded-lg p-2 border border-gray-600 text-white text-sm flex items-center gap-2"
            whileTap={{ scale: 0.98 }}
          >
            ← Zurück
          </motion.button>
          <h3 className="text-lg sm:text-xl font-bold text-white text-center flex-1">
            {getRarityName(selectedType)} → {target?.name}
          </h3>
          <div className="w-16"></div> {/* Spacer für Zentrierung */}
        </div>

        {/* Fortschrittsanzeige */}
        <div className="bg-black/20 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-sm">Ausgewählt:</span>
            <span className="text-white font-bold">{selectedCompanions.length}/5</span>
          </div>
          <div className="w-full bg-black/30 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(selectedCompanions.length / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* Zufällig auswählen Button */}
        {availableCompanions.length >= 5 && (
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <motion.button
              onClick={handleRandomSelect}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 px-4 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="text-lg"
                >
                  🎲
                </motion.div>
                <span className="text-sm sm:text-base">Zufällig auswählen</span>
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="text-lg"
                >
                  🎲
                </motion.div>
              </div>
            </motion.button>
            
            {selectedCompanions.length > 0 && (
              <motion.button
                onClick={handleClearSelection}
                className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white py-3 px-4 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="text-lg"
                  >
                    🔄
                  </motion.div>
                  <span className="text-sm sm:text-base">Alle abwählen</span>
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="text-lg"
                  >
                    🔄
                  </motion.div>
                </div>
              </motion.button>
            )}
          </div>
        )}

        {/* Begleiter-Auswahl Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 p-2">
          {availableCompanions.map((companion) => {
            const isSelected = selectedCompanions.includes(companion.id);
            const canSelect = !isSelected && selectedCompanions.length < 5;
            
            return (
              <motion.div
                key={companion.id}
                onClick={() => handleCompanionToggle(companion.id)}
                className="relative group cursor-pointer"
                whileHover={canSelect || isSelected ? { scale: 1.05, y: -2 } : {}}
                whileTap={canSelect || isSelected ? { scale: 0.95 } : {}}
                layout
              >
                {/* Moderne Glassmorphism-Karte */}
                <div className={`
                  relative bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50 hover:shadow-xl transition-all duration-300 overflow-hidden
                  ${isSelected 
                    ? 'ring-2 ring-green-400 ring-opacity-80 bg-green-500/10' 
                    : canSelect 
                      ? 'hover:bg-white/90' 
                      : 'opacity-50 cursor-not-allowed bg-gray-500/50'
                  }
                `}>
                  {/* Glanzeffekt */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Rarity Glow-Effekt */}
                  <div className={`absolute inset-0 rounded-2xl ${
                    companion.type === 'legendary' ? 'bg-gradient-to-br from-yellow-400/20 via-orange-400/20 to-red-400/20' :
                    companion.type === 'epic' ? 'bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-purple-400/20' :
                    companion.type === 'rare' ? 'bg-gradient-to-br from-blue-400/20 via-cyan-400/20 to-blue-400/20' :
                    'bg-gradient-to-br from-gray-400/20 via-gray-300/20 to-gray-400/20'
                  }`}></div>
                  
                  {/* Ausgewählt-Indikator */}
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-2 -right-2 bg-green-500 rounded-full w-6 h-6 flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-lg z-10"
                    >
                      ✓
                    </motion.div>
                  )}
                  
                  {/* Begleiter-Info */}
                  <div className="relative text-center mb-4">
                    <div className="text-5xl mb-3 drop-shadow-lg">
                      {companion.icon.startsWith('data:') ? (
                        <img 
                          src={companion.icon} 
                          alt={companion.name}
                          className="w-16 h-16 object-contain"
                          style={{ display: 'inline-block' }}
                        />
                      ) : (
                        companion.icon
                      )}
                    </div>
                    <h4 className="text-lg font-bold text-gray-800 leading-tight mb-2 break-words">{companion.name}</h4>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="inline-flex items-center gap-1 bg-gray-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                        <span>📊</span>
                        <span>Level {companion.level || 1}</span>
                      </div>
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${
                        companion.type === 'legendary' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                        companion.type === 'epic' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                        companion.type === 'rare' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                        'bg-gradient-to-r from-gray-500 to-gray-600'
                      }`}>
                        <span>{getRarityName(companion.type)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Fähigkeiten Grid */}
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 mb-4 border border-gray-200/50">
                    <div className="text-xs text-gray-600 font-bold mb-3 flex items-center gap-2">
                      <span>✨</span>
                      <span>Fähigkeiten ({companion.abilities.length})</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {companion.abilities.slice(0, 4).map((ability, index) => (
                        <div key={index} className="bg-white/70 rounded-lg p-2 text-center border border-gray-200/50">
                          <div className="text-xs text-gray-700 font-medium truncate">{getAbilityName(ability.type)}</div>
                          <div className="text-sm font-bold text-gray-800">+{formatNumber(ability.value)}{getAbilitySuffix(ability.type)}</div>
                        </div>
                      ))}
                      {companion.abilities.length > 4 && (
                        <div className="col-span-2 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg p-2 text-center border border-gray-200/50">
                          <div className="text-xs text-gray-600 font-medium">
                            +{companion.abilities.length - 4} weitere Fähigkeiten
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Auswahl-Button */}
                  <div className={`
                    w-full py-2 px-3 rounded-xl font-bold text-sm transition-all duration-300 text-center
                    ${isSelected 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg' 
                      : canSelect 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 shadow-lg' 
                        : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    }
                  `}>
                    {isSelected ? '✓ Ausgewählt' : canSelect ? 'Auswählen' : 'Max erreicht'}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Hinweis */}
        <div className="mt-3 bg-blue-600/20 border border-blue-400 rounded-lg p-2">
          <div className="text-blue-300 text-xs">
            💡 Wähle genau 5 Begleiter für die Fusion aus. Die ausgewählten Begleiter werden permanent verbraucht!
          </div>
        </div>

        {/* Floating Fusion Button - erscheint nur bei 5 ausgewählten Begleitern */}
        <AnimatePresence key="fusion-button">
          {selectedCompanions.length === 5 && (
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.8 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 25,
                duration: 0.3 
              }}
              className="fixed bottom-4 left-4 right-4 z-50 sm:hidden"
            >
              <motion.button
                onClick={handleUpgrade}
                className={`
                  w-full py-4 px-6 rounded-xl font-bold text-lg shadow-2xl border-2
                  bg-gradient-to-r ${getRarityColor(selectedType)} text-white
                  border-white/20 backdrop-blur-sm
                `}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  boxShadow: `0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px ${getRarityColor(selectedType).includes('yellow') ? 'rgba(255, 215, 0, 0.3)' : 
                    getRarityColor(selectedType).includes('purple') ? 'rgba(147, 51, 234, 0.3)' : 
                    getRarityColor(selectedType).includes('blue') ? 'rgba(59, 130, 246, 0.3)' : 
                    'rgba(107, 114, 128, 0.3)'}`
                }}
              >
                <div className="flex items-center justify-center gap-2">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="text-xl"
                  >
                    ⚡
                  </motion.div>
                  <span>Fusion zu {target?.name}!</span>
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="text-xl"
                  >
                    ⚡
                  </motion.div>
                </div>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop Fusion Button - nur auf größeren Bildschirmen sichtbar */}
        <div className="hidden sm:block">
          <motion.button
            onClick={handleUpgrade}
            disabled={selectedCompanions.length !== 5}
            className={`
              w-full py-4 px-6 rounded-xl font-bold text-lg transition-all
              ${selectedCompanions.length === 5
                ? `bg-gradient-to-r ${getRarityColor(selectedType)} text-white shadow-lg hover:shadow-xl`
                : 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
              }
            `}
            whileHover={selectedCompanions.length === 5 ? { scale: 1.02 } : {}}
            whileTap={selectedCompanions.length === 5 ? { scale: 0.98 } : {}}
          >
            {selectedCompanions.length === 5 
              ? `⚡ Fusion zu ${target?.name}!` 
              : `Wähle ${5 - selectedCompanions.length} weitere Begleiter`
            }
          </motion.button>
        </div>
      </div>
    );
  }

  // Standard-Übersicht
  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 sm:p-6 border border-gray-600 mb-4 sm:mb-6">
      {/* Kompakter Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-lg sm:text-xl font-bold text-white flex items-center">
          ⚡ Begleiter-Fusion
        </h3>
        <motion.button
          onClick={() => setShowDetails(!showDetails)}
          className="bg-gray-700/50 rounded-lg p-2 border border-gray-600 text-white text-sm"
          whileTap={{ scale: 0.98 }}
        >
          {showDetails ? '▼' : '▶'} Info
        </motion.button>
      </div>
      
      {/* Collapsible Info */}
      <AnimatePresence key="collapsible-info">
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mb-3 sm:mb-4"
          >
            <div className="bg-blue-600/20 border border-blue-400 rounded-lg p-2 sm:p-3">
              <div className="text-blue-300 text-xs sm:text-sm">
                💡 <strong>Fusion:</strong> Wähle 5 Begleiter eines Typs für 1 stärkeren!
                <div className="text-xs opacity-80 mt-1">
                  Legendäre werden zu göttlichen Versionen mit extremen Stats!
                </div>
                <div className="text-xs opacity-80 mt-1 text-yellow-300">
                  ✨ Göttliche Begleiter (★) sind bereits perfekt.
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mystisch Göttlich Fusion Card */}
      <div className="mb-4 sm:mb-6">
        <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-xl p-4 sm:p-6 border-2 border-purple-400 shadow-lg">
          <div className="text-center text-white mb-4">
            <div className="text-3xl sm:text-4xl mb-2">🔮</div>
            <h3 className="text-lg sm:text-xl font-bold mb-2">Mystisch Göttlich Fusion</h3>
            <p className="text-sm opacity-80 mb-4">
              50 göttlich legendäre Begleiter → 1 mystisch göttlich legendärer Begleiter
            </p>
            
            {(() => {
              const divineCompanions = getDivineLegendaryCompanions();
              const canUpgrade = divineCompanions.length >= 50;
              
              return (
                <div className="space-y-3">
                  <div className="bg-black/30 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm">Göttlich Legendäre:</span>
                      <span className="font-bold">{divineCompanions.length}/50</span>
                    </div>
                    <div className="w-full bg-black/50 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-purple-400 to-pink-400 h-3 rounded-full transition-all"
                        style={{ width: `${Math.min(divineCompanions.length / 50 * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  
                  <motion.button
                    onClick={() => canUpgrade && setShowMysticDivine(true)}
                    disabled={!canUpgrade}
                    className={`w-full py-3 px-4 rounded-xl font-bold text-sm sm:text-base transition-all ${
                      canUpgrade
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
                    }`}
                    whileHover={canUpgrade ? { scale: 1.02 } : {}}
                    whileTap={canUpgrade ? { scale: 0.98 } : {}}
                  >
                    {canUpgrade ? '🔮 Mystisch Göttlich Fusion' : `+${50 - divineCompanions.length} weitere benötigt`}
                  </motion.button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Fusion Cards - Mobile optimiert */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        {upgradeTypes.map((type) => {
          const companions = getCompanionsByType(type);
          const canUpgrade = companions.length >= 5;
          const target = getUpgradeTarget(type);
          
          if (!target) return null;

          return (
            <motion.div
              key={type}
              onClick={() => canUpgrade && handleTypeSelect(type)}
              className={`p-3 sm:p-4 rounded-xl border-2 bg-gradient-to-br ${getRarityColor(type)} ${
                canUpgrade ? 'opacity-100 cursor-pointer' : 'opacity-60 cursor-not-allowed'
              }`}
              whileHover={canUpgrade ? { scale: 1.02 } : {}}
              whileTap={canUpgrade ? { scale: 0.98 } : {}}
            >
              <div className="text-center text-white">
                <div className="text-xl sm:text-2xl mb-1 sm:mb-2">⚡</div>
                <h4 className="font-bold mb-1 text-sm sm:text-base">
                  {getRarityName(type)} → {target.name}
                </h4>
                <div className="text-xs opacity-80 mb-2 sm:mb-3">
                  {canUpgrade ? 'Fusion verfügbar' : 'Mehr sammeln'}
                </div>
                
                <div className="bg-black/20 rounded-lg p-1 sm:p-2 mb-2 sm:mb-3">
                  <div className="text-xs">
                    {companions.length}/5
                  </div>
                  <div className="w-full bg-black/30 rounded-full h-1 sm:h-2 mt-1">
                    <div 
                      className="bg-white/60 h-1 sm:h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(companions.length / 5 * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {type === 'legendary' && (
                  <div className="text-xs bg-gradient-to-r from-yellow-500/30 to-orange-500/30 rounded p-1 mb-2 border border-yellow-400/50">
                    ✨ Göttlich! ✨
                  </div>
                )}

                <div className={`py-2 px-2 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-colors ${
                  canUpgrade
                    ? 'bg-white/20 text-white'
                    : 'bg-black/20 text-white/50'
                }`}>
                  {canUpgrade ? 'Auswählen' : `+${5 - companions.length}`}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>


    </div>
  );
};

export default CompanionUpgrade; 